// noinspection JSUnusedGlobalSymbols
import { trace } from "@opentelemetry/api";
import ellipsize from "ellipsize";
import { freeze } from "immer";
import latinize from "latinize";
import LRUCache from "lru-cache";
import { chunk, cloneDeep, escapeRegExp, extend, groupBy, head, isArray, isNil, isNull, isPlainObject, isUndefined, keyBy, memoize, sortBy, uniq, zip, } from "lodash-es";
import { stripHtml } from "string-strip-html";
import { z } from "zod";
export const DEFAULT_IDENTITY_LOCALE = "en";
export const UNKNOWN_DEFAULT_IDENTITY_LOCALE = DEFAULT_IDENTITY_LOCALE;
export const DateOrISOStringSchema = z.union([z.date(), z.string()]);
export function assertNever(x, message) {
    throw new Error(message ?? "Unexpected object: " + x);
}
function throwError(errorMessage, errorConstructor) {
    throw new (errorConstructor ?? Error)(errorMessage);
}
export function decodeGlobalId(encodedGlobalId) {
    return { id: encodedGlobalId };
}
export function encodeGlobalId(_type, id) {
    return id;
}
export function stringOrThrow(str) {
    if (str === undefined || str === null)
        throw new Error("undefined or null");
    return str;
}
export const UTC_OFFSET = 120;
export function pick(base, ...keys) {
    const entries = keys.map(key => [key, base[key]]);
    return Object.fromEntries(entries);
}
export function omit(base, ...keys) {
    let obj = { ...base };
    for (let key of keys) {
        delete obj[key];
    }
    return obj;
}
export function omitDeep(collection, ...excludeKeys) {
    const clonedCollection = cloneDeep(collection);
    function omitFn(value) {
        if (value && isPlainObject(value)) {
            excludeKeys.forEach(key => {
                delete value[key];
            });
        }
    }
    function traverse(value) {
        if (isArray(value)) {
            value.forEach(traverse);
        }
        else if (value && isPlainObject(value)) {
            omitFn(value);
            Object.keys(value).forEach(key => traverse(value[key]));
        }
    }
    traverse(clonedCollection);
    return clonedCollection;
}
export function findOrThrow(arr, predicate, notFoundErrorMessage, errorConstructor) {
    const result = arr.find(predicate);
    if (!result) {
        throwError(notFoundErrorMessage ?? `not found`, errorConstructor);
    }
    return result;
}
export const MAX_LIMIT = 99999;
export function foundOrThrow(loadResult, errorMessage) {
    if (loadResult.type === "found")
        return loadResult.aggregate;
    throw new Error(errorMessage ?? "not found");
}
export function throwIfNull(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage, errorConstructor) {
    if (value === null) {
        throwError(errorMessage ?? `not expected null value`, errorConstructor);
    }
    return value;
}
export function throwIfNil(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage) {
    if (isNil(value))
        throw new Error(errorMessage ?? `not expected nil value`);
    return value;
}
export function throwIfUntruthy(value, errorMessage) {
    if (!value)
        throw new Error(errorMessage ?? `not expected nil value`);
    return value;
}
export function throwIfEmptyString(value, errorMessage, errorConstructor) {
    if (value.length === 0) {
        throwError(errorMessage ?? `not expected empty string`, errorConstructor);
    }
    return value;
}
export function throwIfUndefined(
// value: (T extends Promise<infer X> ? never : T) | null
value, errorMessage, errorConstructor) {
    if (isUndefined(value)) {
        throwError(errorMessage ?? `not found`, errorConstructor);
    }
    return value;
}
export function throwIfAnyNull(
// value: (T extends Promise<infer X> ? never : T) | null
values, errorMessage, errorConstructor) {
    const nonNullValues = filterNonNull(values);
    if (values.length !== nonNullValues.length) {
        throwError(errorMessage ?? `not expected null value in "${values}"`, errorConstructor);
    }
    return nonNullValues;
}
export function headOrThrow(values, errorMessage, errorConstructor) {
    return throwIfUndefined(head(values), errorMessage, errorConstructor);
}
export function valuesByKeys(values, keys, valueKey) {
    const valuesMap = keyBy(values, value => valueKey(value));
    return keys.map(key => valuesMap[key] ?? null);
}
export function groupedValuesByKeys(values, keys, valueKey) {
    const valuesMap = groupBy(values, value => valueKey(value));
    return keys.map(key => valuesMap[key] ?? []);
}
export function sortedGroupedValuesByKeys(values, keys, valueKey, iteratee // FIXME
) {
    const valuesMap = groupBy(values, value => valueKey(value));
    return keys.map(key => sortBy(valuesMap[key], iteratee) ?? []);
}
export function isPicture(mimeType) {
    return mimeType.toLowerCase().startsWith("image");
}
export function getPictures(attachments) {
    return attachments.filter(e => isPicture(e.mimeType));
}
export function getFiles(attachments) {
    return attachments.filter(e => !isPicture(e.mimeType));
}
export function getNameWithCopy(name, copySuffix) {
    // const regexp = new RegExp(`^(.*) - ${copySuffix}( [0-9]+)?$`, "gi");
    // const [, originalName] = name.match(regexp) ?? [];
    // return `${originalName ?? name} - ${copySuffix}`;
    return `${name} - ${copySuffix}`;
}
// export type DeepReadonly<T> = {
//   readonly [P in keyof T]: DeepReadonly<T[P]>;
// };
export function wrapErrorWithContext(cause, context) {
    const message = [
        ...(cause?.message?.split(";") ?? []),
        ...Object.entries(context).map(([key, val]) => `${key}: ${val}`),
    ].join(";");
    throw new Error(message, { cause });
}
export function enhanceWithFullName(subject) {
    return {
        ...subject,
        fullname: `${subject.firstname} ${subject.familyname}`.trim(),
    };
}
export function notNull(value) {
    return value !== null;
}
export function notUndefined(value) {
    return value !== undefined;
}
export function notNullNorUndefined(value) {
    return value !== null && value !== undefined;
}
export function requiredValue(value, valueOnUndefined) {
    return isUndefined(value) ? valueOnUndefined : value;
}
export function nonNilValue(value, valueOnUndefined, valueOnNull) {
    return isUndefined(value)
        ? valueOnUndefined
        : isNull(value)
            ? valueOnNull
            : value;
}
export const parseNumber = (number, prefix, minLength) => {
    const regexp = new RegExp(`^${prefix
        .replace(/<YYYY>/gi, "[0-9]{4}")
        .replace(/<YY>/gi, "[0-9]{2}")
        .replace(/<MM>/gi, "[0-9]{2}")
        .replace(/<DD>/gi, "[0-9]{2}")}([0-9]+)$`);
    const [, counter] = number.match(regexp) ?? [];
    if (!counter)
        throw new Error(`Number ${number} does not match provided prefix: ${prefix}`);
    if (counter.length < minLength)
        throw new Error(`Counter ${counter} length is smaller than min length: ${minLength}`);
    return parseInt(counter.replace(/^0+/, ""), 10);
};
export async function wait(ms) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
}
export function zipOrThrow(array1, array2) {
    if (array1.length !== array2.length) {
        throw new Error(`Arrays have different lengths ${array1.length} and ${array2.length}`);
    }
    return zip(array1, array2);
}
export function zipOrThrow3(array1, array2, array3) {
    if (uniq([array1.length, array2.length, array3.length]).length !== 1) {
        throw new Error(`Arrays have different lengths ${array1.length} and ${array2.length} and ${array3.length}`);
    }
    return zip(array1, array2, array3);
}
export function filterNonNull(array) {
    return array.filter(notNull);
}
export async function doBy(array, mapValueFn, fn) {
    const valuesMapped = filterNonNull(array.map((value, index) => {
        const mapped = mapValueFn(value);
        return mapped ? { value: mapped, index } : null;
    }));
    const results = await fn(valuesMapped.map(e => e.value));
    return valuesByKeys(zipOrThrow(results, valuesMapped).map(([result, { index }]) => ({
        result,
        index,
    })), array.map((_, index) => index), e => e.index).map(e => (e ? e.result : null));
}
export function doBySync(array, mapValueFn, fn) {
    const valuesMapped = filterNonNull(array.map((value, index) => {
        const mapped = mapValueFn(value);
        return mapped ? { value: mapped, index } : null;
    }));
    const results = fn(valuesMapped.map(e => e.value));
    return valuesByKeys(zipOrThrow(results, valuesMapped).map(([result, { index }]) => ({
        result,
        index,
    })), array.map((_, index) => index), e => e.index).map(e => (e ? e.result : null));
}
export function filterNonNil(array) {
    return array.filter(notNullNorUndefined);
}
export const stripHtmlTags = (html = "", keepLineBreaks = true) => {
    return stripHtml(keepLineBreaks
        ? html
            .replace(/<br ?\/?>/gi, `<br/>\n`)
            .replace(/<\/(br|p|li|h1|h2|h3|h4|h5|h6)>/gi, `</$1>\n`)
        : html).result;
};
export const nl2br = (text) => text.replace(/\r\n|\r|\n/g, "<br/>");
export const truncateText = (text, maxSymbolsAmount) => ellipsize(text, maxSymbolsAmount);
export const deepFreeze = (obj) => {
    const span = trace
        .getTracer(process.env.SERVICE_NAME ?? "undefined")
        .startSpan("msys_deepFreeze");
    try {
        return freeze(obj, true);
    }
    finally {
        span.end();
    }
};
/**
 * Assume a float with country specific thousand grouper and country specific decimal, removes the grouper and replaces the decimal with "."
 */
export function parseCountryFloat(value, opts) {
    let cleanedValue = value;
    if (opts.grouper) {
        cleanedValue = cleanedValue.replace(new RegExp(escapeRegExp(opts.grouper), "g"), "");
    }
    cleanedValue = cleanedValue.replace(new RegExp(escapeRegExp(opts.decimal), "g"), ".");
    const parsedValue = parseFloat(cleanedValue);
    if (Number.isNaN(parsedValue) || !Number.isFinite(parsedValue)) {
        throw new Error(`Not a valid number "${parsedValue}" from "${value}"`);
    }
    return parsedValue;
}
export class ConcurrentDocumentModificationError extends Error {
}
// modify the behavior for German umlauts
// noinspection NonAsciiCharacters
extend(latinize.characters, {
    Ä: "Ae",
    Ö: "Oe",
    Ü: "Ue",
    ä: "ae",
    ö: "oe",
    ü: "ue",
});
export function getFileName(name, spacer = "-") {
    return latinize(name)
        .replace(/[^0-9a-zA-Z ]/gi, "")
        .replace(/\s+/gi, spacer);
}
export function getLocaleLanguageAndCountry(locale) {
    const [language, country] = locale.split("-");
    return { language, country };
}
export function getFilenameFromContentDisposition(contentDisposition = "") {
    const regex = /filename\*?=([^']*'')?([^;]*)/;
    return regex.exec(contentDisposition)?.[2].replaceAll('"', "");
}
export async function streamToBuffer(stream) {
    const buffers = [];
    // node.js readable streams implement the async iterator protocol
    for await (const data of stream) {
        buffers.push(Buffer.from(data));
    }
    return Buffer.concat(buffers);
}
export function* bufferToBufferChunkIterator(buffer, chunkSize) {
    for (let i = 0; i < buffer.length; i = i + chunkSize) {
        yield buffer.subarray(i, i + chunkSize);
    }
}
export function* arrayToChunkIterator(array, chunkSize) {
    for (const c of chunk(array, chunkSize)) {
        yield c;
    }
}
function isNotError(value) {
    return (value.__typename !== "MissingCapabilities" &&
        value.__typename !== "MissingPermissions");
}
export function getDataOrNull(value) {
    if (value === undefined)
        return null;
    return isNotError(value) ? value : null;
}
export function getDataOrThrow(value) {
    if (!isNotError(value))
        throw new Error(value.__typename);
    return value;
}
export function dateOrIsoString2DateIsoString(value) {
    return typeof value === "string" ? value : value.toISOString();
}
export function memoizeLru(fn, resolver, cacheOpts) {
    const memoized = memoize(fn, resolver);
    memoized.cache = new LRUCache(cacheOpts);
    return memoized;
}
//# sourceMappingURL=common.js.map